import { getToken } from './auth';
import { fetchZoomSessionJoinStatus } from '../api/appointmentsApi';
import { appointmentTimeBucket, zoomJoinErrorMessage } from './appointmentHelpers';

export function appointmentTypeOf(appointment) {
  if (!appointment) return '';
  const raw = appointment.appointmentType ?? appointment.appointment_type ?? '';
  return String(raw).trim();
}

export function isVideoAppointmentType(appointment) {
  const t = appointmentTypeOf(appointment).toLowerCase();
  return t === 'online' || t === 'telehealth' || t === 'video';
}

export function patientMrnOf(appointment) {
  if (!appointment) return null;
  const v = appointment.PMRN ?? appointment.pmrn ?? appointment.MRN;
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

export function getSessionNameForAppointment(appointment) {
  const explicitSession = String(appointment?.meeting_url || '').trim();
  if (explicitSession) return explicitSession;
  if (!isVideoAppointmentType(appointment) || appointment?.id == null) return '';
  return `appointment-${appointment.id}`;
}

export function isVideoAppointment(appointment) {
  return !!getSessionNameForAppointment(appointment);
}

export function isTerminalAppointmentStatus(appointment) {
  const status = String(appointment?.status || '').trim();
  const conf = String(appointment?.confirmation_status || '').trim();
  if (
    status === 'Completed' ||
    status === 'Cancelled' ||
    status === 'No-Show' ||
    status === 'No-show' ||
    status === 'Requested'
  ) {
    return true;
  }
  if (conf === 'Declined' || conf === 'Cancelled') {
    return true;
  }
  return false;
}

export function canJoinVideoAppointment(appointment) {
  if (!isVideoAppointment(appointment)) return false;
  if (isTerminalAppointmentStatus(appointment)) return false;
  if (appointmentTimeBucket(appointment) === 'past') return false;
  return true;
}

export function onlineConsultationPath(sessionName, patientMrn) {
  const q = patientMrn != null ? `&patientMrn=${encodeURIComponent(patientMrn)}` : '';
  return `/online-consultation?session=${encodeURIComponent(sessionName)}${q}`;
}

export async function checkZoomJoinEligibility({
  apiBaseUrl,
  token,
  sessionName,
  patientMrn,
}) {
  if (!sessionName) {
    return { ok: false, reason: 'No video session for this appointment.', code: 'no_session' };
  }
  if (!token) {
    return { ok: false, reason: 'Not signed in.', code: 'not_signed_in' };
  }
  try {
    const status = await fetchZoomSessionJoinStatus(
      apiBaseUrl,
      token,
      sessionName,
      patientMrn
    );
    if (status && status.can_join === false) {
      const code = status.join_blocked_reason || null;
      const reason =
        (status.message && String(status.message).trim()) ||
        zoomJoinErrorMessage(code);
      return {
        ok: false,
        reason,
        code: code || 'blocked',
        status,
      };
    }
    return { ok: true, status: status || {} };
  } catch (err) {
    return {
      ok: false,
      reason: err?.message || 'Could not check join eligibility.',
      code: 'status_error',
    };
  }
}

export async function startZoomForAppointment({
  apiBaseUrl,
  appointment,
  navigate,
  onBlocked,
  onError,
}) {
  const sessionName = getSessionNameForAppointment(appointment);
  if (!sessionName) {
    onError?.('No video session for this appointment');
    return false;
  }
  const patientMrn = patientMrnOf(appointment);
  const token = getToken();
  const result = await checkZoomJoinEligibility({
    apiBaseUrl,
    token,
    sessionName,
    patientMrn,
  });
  if (!result.ok) {
    if (result.code === 'not_signed_in' || result.code === 'no_session' || result.code === 'status_error') {
      onError?.(result.reason);
    } else {
      onBlocked?.(result.reason, result.code);
    }
    return false;
  }
  navigate(onlineConsultationPath(sessionName, patientMrn));
  return true;
}
